import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { MetricCard } from '../MetricCards';
import EmptyState from '../EmptyState';
import LoadErrorBox from '../ui/LoadErrorBox';
import { createDoorApi, staffCredentials } from '../../services/DoorApiClient';
import type { DoorFavour } from '../../types/Door';

interface FavoursViewProps {
  eventId?: string | null;
}

const api = createDoorApi(staffCredentials);

export interface FavourTotals { name: string; due: number; given: number }

/** Totals per favour name: how many are due to this event's guests and how many are already handed over. */
export function totalsByFavour(favours: readonly DoorFavour[]): FavourTotals[] {
  const byName = new Map<string, FavourTotals>();
  for (const f of favours) {
    const t = byName.get(f.favour_name) ?? { name: f.favour_name, due: 0, given: 0 };
    byName.set(f.favour_name, { ...t, due: t.due + f.quantity, given: t.given + (f.handed_over ? f.quantity : 0) });
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Favours for the chosen event at a glance. Handing one over is done per guest on the Check-in tab. */
const FavoursView: React.FC<FavoursViewProps> = ({ eventId }) => {
  const [favours, setFavours] = useState<DoorFavour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    try {
      setFavours(await api.listFavours(eventId));
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setLoading(true);
    void load();
    const timer = setInterval(() => { if (!document.hidden) void load(); }, 15_000);
    return () => clearInterval(timer);
  }, [load]);

  const totals = useMemo(() => totalsByFavour(favours), [favours]);
  const due = totals.reduce((n, t) => n + t.due, 0);
  const given = totals.reduce((n, t) => n + t.given, 0);

  if (!eventId) {
    return <EmptyState icon="calendar" title="Choose an event" description="Favours are per event. Pick the event you are running at the top of the page." />;
  }
  if (loading) {
    return <div className="ed-loading" role="status"><RefreshCw size={24} className="ed-spin" aria-hidden /><p>Loading favours…</p></div>;
  }
  if (error && favours.length === 0) {
    return <div className="wt-container"><LoadErrorBox what="the favours" error={error} onRetry={() => { void load(); }} /></div>;
  }

  return (
    <div className="wt-container fade-in">
      {error != null && <p className="ed-notice" role="status">Not connected. Showing the last favours received.</p>}
      <div className="stats-grid">
        <MetricCard label="Due to guests" value={due} tone="blue" icon="gift" />
        <MetricCard label="Handed over" value={given} tone="green" icon="user-check" />
        <MetricCard label="Still to give" value={due - given} tone="orange" icon="package" />
      </div>
      {totals.length === 0 ? (
        <EmptyState compact title="No favours for this event yet" description="Favours assigned to guests invited to this event appear here." />
      ) : (
        <table className="ed-favour-table">
          <caption className="door-sr">Favours for this event</caption>
          <thead><tr><th scope="col">Favour</th><th scope="col">Due</th><th scope="col">Handed over</th><th scope="col">Left</th></tr></thead>
          <tbody>
            {totals.map(t => (
              <tr key={t.name}><th scope="row">{t.name}</th><td>{t.due}</td><td>{t.given}</td><td>{t.due - t.given}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="form-helper">Hand a favour over to a guest from the Check-in tab.</p>
    </div>
  );
};

export default FavoursView;
